import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ReconnectingWebSocket from "reconnecting-websocket";
import clienteAxios from "../../config/axios";

const PTS2_SOCKET_URL =
  process.env.REACT_APP_PTS2_SOCKET_URL || "wss://pts2-gas-ultra.neitor.com/ws";

const PRODUCT_COLORS = {
  "DIESEL PREMIUM": { color: "#FFB300", abrev: "DSP" },
  "DIESEL PREMIUN": { color: "#FFB300", abrev: "DSP" },
  "GASOLINA EXTRA": { color: "#039BE5", abrev: "EXT" },
  "GASOLINA SUPER": { color: "#00897B", abrev: "SUP" },
};
const DEFAULT_PRODUCT = { color: "#607D8B", abrev: "" };

const ESTADO_INFO = {
  L: { color: "#2E7D32", label: "Libre", icon: "✓" },
  B: { color: "#C62828", label: "Bloqueado", icon: "⊘" },
  A: { color: "#1565C0", label: "Abasteciendo", icon: "⥁" },
  E: { color: "#EF6C00", label: "En espera", icon: "⏱" },
  P: { color: "#6A1B9A", label: "Preparando", icon: "⚙" },
  F: { color: "#B71C1C", label: "Falla", icon: "!" },
  C: { color: "#00838F", label: "Finalizado", icon: "✔" },
  "#": { color: "#455A64", label: "Ocupado", icon: "◙" },
  "!": { color: "#4E342E", label: "Error", icon: "⚠" },
};
const ESTADO_DESCONOCIDO = { color: "#90A4AE", label: "Desconocido", icon: "?" };
const LIVE_COLOR = "#00C853";

const getProductInfo = (nombreProducto) =>
  PRODUCT_COLORS[String(nombreProducto || "").toUpperCase()] || DEFAULT_PRODUCT;

const getEstadoInfo = (estadoChar) =>
  ESTADO_INFO[estadoChar] || ESTADO_DESCONOCIDO;

const estimateBrightnessForColor = (hex) => {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const lin = [r, g, b].map((v) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4),
  );
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
};

const isLight = (hex) => estimateBrightnessForColor(hex) > 0.5;

const buildEstacionesManguera = (surtidores) => {
  const items = [];
  surtidores.forEach((surtidor) => {
    const lado = Number(surtidor.lado);
    if (!Number.isFinite(lado)) return;
    const estaciones = [surtidor.estacion1, surtidor.estacion2, surtidor.estacion3];
    estaciones.forEach((est) => {
      if (!est || !est.nombre_producto) return;
      items.push({
        lado,
        nozzle: Number(est.numero_pistola),
        numeroPistola: est.numero_pistola,
        codigoProducto: est.codigo_producto,
        nombreProducto: est.nombre_producto,
        numeroTanque: est.numero_tanque,
        nombreSurtidor: surtidor.nombre_surtidor,
      });
    });
  });
  items.sort((a, b) =>
    a.lado === b.lado ? a.nozzle - b.nozzle : a.lado - b.lado,
  );
  return items;
};

const DispensadoresScreen = () => {
  const history = useHistory();

  const [surtidores, setSurtidores] = useState(null);
  const [manguerasStatus, setManguerasStatus] = useState(null);
  const [liveVisualizationList, setLiveVisualizationList] = useState([]);

  useEffect(() => {
    const fetchSurtidores = async () => {
      try {
        const res = await clienteAxios.get("/surtidores/todo");
        setSurtidores(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        setSurtidores([]);
      }
    };
    fetchSurtidores();
  }, []);

  useEffect(() => {
    const statusWs = new ReconnectingWebSocket(
      `${PTS2_SOCKET_URL}/status_mangueras`,
    );
    const liveWs = new ReconnectingWebSocket(
      `${PTS2_SOCKET_URL}/despachos_visualizacion_mangueras`,
    );

    statusWs.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type !== "pico_status") return;
        setManguerasStatus(message.data || {});
      } catch (e) {}
    };

    liveWs.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type !== "live_visualization") return;
        setLiveVisualizationList(Array.isArray(message.data) ? message.data : []);
      } catch (e) {}
    };

    statusWs.onerror = () => {};
    liveWs.onerror = () => {};

    return () => {
      statusWs.close();
      liveWs.close();
    };
  }, []);

  const mangueras = useMemo(
    () => (surtidores ? buildEstacionesManguera(surtidores) : []),
    [surtidores],
  );

  const findVisualization = (lado, nozzle) => {
    const item = liveVisualizationList.find(
      (v) => Number(v.lado) === Number(lado) && Number(v.nozzle) === Number(nozzle),
    );
    return {
      pico: item ? item.pico : null,
      valorActual: item ? Number(item.valor_actual) || 0 : 0,
    };
  };

  const gruposPorLado = useMemo(() => {
    const map = new Map();
    mangueras.forEach((m) => {
      if (!map.has(m.lado)) map.set(m.lado, []);
      map.get(m.lado).push(m);
    });
    const resultado = [];
    Array.from(map.keys())
      .sort((a, b) => a - b)
      .forEach((lado) => {
        const items = map.get(lado).slice().sort((a, b) => {
          const estadoA =
            (manguerasStatus?.[String(a.lado)]?.[String(a.nozzle)] || "") === "A";
          const estadoB =
            (manguerasStatus?.[String(b.lado)]?.[String(b.nozzle)] || "") === "A";
          if (estadoA !== estadoB) return estadoA ? 1 : -1;
          return a.nozzle - b.nozzle;
        });
        resultado.push({ lado, items });
      });
    return resultado;
  }, [mangueras, manguerasStatus]);

  const navigateToManguera = (m) => {
    const codigoFormateado = String(m.codigoProducto || "").padStart(4, "0");
    history.push(
      `/info_manguera?manguera=${encodeURIComponent(
        m.numeroPistola,
      )}&codigoProducto=${encodeURIComponent(codigoFormateado)}&lado=${
        m.lado
      }&nozzle=${m.nozzle}`,
    );
  };

  if (!surtidores) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (manguerasStatus === null) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={4}>
        <CircularProgress size={28} sx={{ marginBottom: "0.5rem" }} />
        <Typography variant="body2" color="text.secondary">
          Esperando estado de mangueras...
        </Typography>
      </Box>
    );
  }

  if (mangueras.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          No hay dispensadores configurados.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {gruposPorLado.map((grupo) => {
        const nombreSurtidor = grupo.items[0]?.nombreSurtidor || "";
        return (
          <Grid item xs={12} sm={6} md={4} key={`lado-${grupo.lado}`}>
            <Card
              elevation={3}
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#263238",
                  color: "#fff",
                  padding: "0.6rem 1rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  LADO {grupo.lado}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {grupo.items.length} mangueras
                  </Typography>
                  {nombreSurtidor && (
                    <Typography
                      variant="caption"
                      sx={{
                        backgroundColor: "rgba(255,255,255,0.12)",
                        padding: "0.1rem 0.4rem",
                        borderRadius: "4px",
                      }}
                    >
                      {nombreSurtidor}
                    </Typography>
                  )}
                </Box>
              </Box>
              <CardContent sx={{ padding: "0.5rem 0.75rem" }}>
                {grupo.items.map((m) => {
                  const estadoChar =
                    manguerasStatus?.[String(m.lado)]?.[String(m.nozzle)] || "";
                  const estado = getEstadoInfo(estadoChar);
                  const vis = findVisualization(m.lado, m.nozzle);
                  const prod = getProductInfo(m.nombreProducto);
                  const enVivo = vis.valorActual !== 0;
                  const badgeBg = enVivo ? LIVE_COLOR : estado.color;
                  const badgeFg = isLight(badgeBg) ? "#000" : "#fff";
                  const prodFg = isLight(prod.color) ? "#000" : "#fff";

                  return (
                    <Box
                      key={`m-${m.lado}-${m.nozzle}`}
                      onClick={() => navigateToManguera(m)}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0.4rem 0.25rem",
                        borderRadius: "8px",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                        <Box
                          sx={{
                            backgroundColor: prod.color,
                            color: prodFg,
                            borderRadius: "6px",
                            padding: "0.15rem 0.4rem",
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                            minWidth: "2.2rem",
                            textAlign: "center",
                            flexShrink: 0,
                          }}
                        >
                          M{m.nozzle}
                        </Box>
                        <Box minWidth={0}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {m.nombreProducto}
                          </Typography>
                          {m.numeroTanque !== undefined &&
                            m.numeroTanque !== null &&
                            m.numeroTanque !== "" && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", lineHeight: 1 }}
                              >
                                Tanque {m.numeroTanque}
                              </Typography>
                            )}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          backgroundColor: badgeBg,
                          color: badgeFg,
                          borderRadius: "8px",
                          padding: "0.25rem 0.5rem",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          minWidth: "5rem",
                          flexShrink: 0,
                        }}
                      >
                        {enVivo ? (
                          <>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: "bold", lineHeight: 1 }}
                            >
                              EN VIVO
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: "bold", lineHeight: 1.2 }}
                            >
                              ${vis.valorActual.toFixed(2)}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: "bold", lineHeight: 1 }}
                            >
                              {estado.icon} {estado.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ lineHeight: 1, opacity: 0.85 }}
                            >
                              {estadoChar || "—"}
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <Divider sx={{ marginTop: "0.5rem" }} />
      </Grid>
    </Grid>
  );
};

export default DispensadoresScreen;